import React from 'react';
import { ChanceyCard, NumberRow } from '../components/chancey';
import { SupportPageLayout } from '../components/layout/SupportPageLayout';
import { useGameApi } from '../api/games';
import type { GameDrawHistoryResponse } from '../api/types';
import { formatUsd } from '../data/lotteryData';
import { useActiveGame } from '../hooks/useActiveGame';
import { useChanceyTheme } from '../theme/ThemeContext';
import { friendlyErrorMessage } from '../utils/errorCopy';

const PAGE_SIZE = 20;

const DrawHistoryPage: React.FC = () => {
  const { c, typ } = useChanceyTheme();
  const { game, gameId } = useActiveGame();
  const { getGameDrawHistory } = useGameApi();
  const [page, setPage] = React.useState(1);
  const [data, setData] = React.useState<GameDrawHistoryResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void getGameDrawHistory(gameId, page, PAGE_SIZE)
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(friendlyErrorMessage(err, 'Could not load draw history'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gameId, getGameDrawHistory, page]);

  React.useEffect(() => {
    setPage(1);
  }, [gameId]);

  const totalPages = data?.totalPages ?? 1;

  return (
    <SupportPageLayout title="Draw History" eyebrow={game.name} fallbackPath="/home">
      {error ? (
        <ChanceyCard c={c} pad={16}>
          <div className="text-sm font-semibold" style={{ color: c.ink, fontFamily: typ.body }}>
            Couldn’t load draw history
          </div>
          <div className="mt-1 text-[13px]" style={{ color: c.inkSoft, fontFamily: typ.body }}>
            {error}
          </div>
        </ChanceyCard>
      ) : null}

      <div className="flex flex-col gap-3">
        {loading && !data ? (
          <DrawSkeleton c={c} />
        ) : data && data.draws.length > 0 ? (
          data.draws.map((draw) => (
            <ChanceyCard key={draw.iso} c={c} pad={16}>
              <div className="mb-3 flex items-center justify-between gap-3">
                <div
                  className="font-mono text-[13px] font-semibold"
                  style={{ color: c.ink, fontFamily: typ.mono }}
                >
                  {formatIsoDate(draw.iso)}
                </div>
                <div className="text-[12px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
                  {formatUsd(draw.jackpot)}
                </div>
              </div>
              <NumberRow main={draw.main} bonus={draw.bonus} size={32} gap={6} c={c} />
            </ChanceyCard>
          ))
        ) : (
          <ChanceyCard c={c} pad={16}>
            <div className="text-sm" style={{ color: c.inkSoft, fontFamily: typ.body }}>
              No draw history is available yet.
            </div>
          </ChanceyCard>
        )}
      </div>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        loading={loading}
        onPrev={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => p + 1)}
        c={c}
        typ={typ}
      />
    </SupportPageLayout>
  );
};

export default DrawHistoryPage;

function DrawSkeleton({ c }: { c: ReturnType<typeof useChanceyTheme>['c'] }) {
  return (
    <ChanceyCard c={c} pad={16}>
      <div className="flex gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-10 w-10 rounded-full" style={{ background: c.surfaceAlt, opacity: 0.7 }} />
        ))}
      </div>
    </ChanceyCard>
  );
}

function PaginationControls({
  page,
  totalPages,
  loading,
  onPrev,
  onNext,
  c,
  typ,
}: {
  page: number;
  totalPages: number;
  loading: boolean;
  onPrev: () => void;
  onNext: () => void;
  c: ReturnType<typeof useChanceyTheme>['c'];
  typ: ReturnType<typeof useChanceyTheme>['typ'];
}) {
  return (
    <div className="flex items-center justify-between gap-3 pt-1">
      <button
        type="button"
        onClick={onPrev}
        disabled={loading || page <= 1}
        className="rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-45"
        style={{ background: c.surface, color: c.ink, border: `0.5px solid ${c.line}`, fontFamily: typ.body }}
      >
        Previous
      </button>
      <div className="text-xs" style={{ color: c.inkMuted, fontFamily: typ.body }}>
        Page {page} of {totalPages}
      </div>
      <button
        type="button"
        onClick={onNext}
        disabled={loading || page >= totalPages}
        className="rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-45"
        style={{ background: c.ink, color: c.bg, fontFamily: typ.body }}
      >
        Next
      </button>
    </div>
  );
}

function formatIsoDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}
